import { PriceHistory } from '@api/api/price-schema';
import { add, format } from 'date-fns';

import {
  computePriceMetrics,
  DATE_FORMAT,
  DailyFallbackRange,
  VariantDataResult,
} from './types';

/**
 * Absolute UTC bounds of the fallback window, anchored on the insight's New
 * York calendar day so they line up with the daily bars' own midnight-UTC
 * timestamps. `reportingDateET` is a naive ET date, so reading and shifting
 * its calendar fields keeps the anchor on the ET day across a DST boundary.
 *
 * `{ months: 1, days: -1 }` is the forward mirror of the chart's own 1M
 * horizon in `getStartOfRangeHorizon`, so a month spans the same days here as
 * it does when you pick 1M on the chart.
 */
export const getDailyFallbackRange = (
  reportingDateET: Date
): DailyFallbackRange => ({
  dateFrom: format(reportingDateET, `${DATE_FORMAT}'T00:00:00Z'`),
  dateTo: format(
    add(reportingDateET, { months: 1, days: -1 }),
    `${DATE_FORMAT}'T23:59:59Z'`
  ),
});

/**
 * End-of-day stand-in for insights whose intraday ticks have aged out of the
 * price feed. Charts one month of daily closes from the insight day, reusing
 * the daily series already fetched to resolve the previous close.
 *
 * Window bounds are the same UTC-midnight instants as the daily bars.
 * Format labels in UTC (`MMM d`), not ET — a US timezone would otherwise
 * print the previous calendar day.
 */
export const getDailyFallbackData = ({
  dailyFallbackRange,
  dailyHistoryData,
  isLoading,
}: {
  dailyFallbackRange: DailyFallbackRange;
  dailyHistoryData?: PriceHistory;
  isLoading: boolean;
}): VariantDataResult => {
  const fromDay = dailyFallbackRange.dateFrom.slice(0, 10);
  const toDay = dailyFallbackRange.dateTo.slice(0, 10);

  // Compare calendar days, not the full timestamps: a bar stamped with
  // milliseconds sorts below the same instant without them ('.' < 'Z'), so a
  // whole-string compare would drop the window's first bar.
  const priceData = (dailyHistoryData ?? [])
    .filter(bar => {
      const day = bar.time.slice(0, 10);
      return day >= fromDay && day <= toDay;
    })
    .map(bar => ({ time: bar.time, value: bar.close }));

  return {
    priceData,
    fromDateEST: new Date(priceData[0]?.time ?? dailyFallbackRange.dateFrom),
    toDateEST: new Date(priceData.at(-1)?.time ?? dailyFallbackRange.dateTo),
    ...computePriceMetrics(priceData, dailyHistoryData),
    isDailyFallback: true,
    dailyFallbackRange,
    isLoading,
  };
};
